import { createPuzzleBotInput } from "../bot";
import {
  PUZZLE_EMPTY_INPUT,
  PUZZLE_TICK_RATE,
  createPuzzleMatchState,
  resetPuzzleMatchForDemo,
  stepPuzzleMatch,
  type PuzzleEvent,
  type PuzzleBotDifficulty,
  type PuzzleInput,
  type PuzzleMatchState,
  type PuzzleMode,
  type PuzzlePlayerSlot
} from "../simulation";

export interface LocalPuzzleDriverOptions {
  mode: PuzzleMode;
  readHumanInput: () => PuzzleInput;
  onSnapshot: (state: PuzzleMatchState) => void;
  onEvents?: (events: PuzzleEvent[]) => void;
  /** Spectator previews can ask the bot to drive the nominal human seat. */
  automateHuman?: boolean;
  /** Difficulty shared by local bots and any automated nominal human seat. */
  botDifficulty?: PuzzleBotDifficulty;
  seed?: number;
}

const DUEL_SLOTS: PuzzlePlayerSlot[] = [
  { id: "p1", name: "NOVA", team: "blue", kind: "human", connected: true },
  { id: "p2", name: "KITE", team: "orange", kind: "bot", connected: true }
];

const TEAM_SLOTS: PuzzlePlayerSlot[] = [
  { id: "p1", name: "NOVA", team: "blue", kind: "human", connected: true },
  { id: "p2", name: "RIFT", team: "orange", kind: "bot", connected: true },
  { id: "p3", name: "HEX", team: "blue", kind: "bot", connected: true },
  { id: "p4", name: "VELA", team: "orange", kind: "bot", connected: true }
];

/** Browser-only 30 Hz authority for the landing-page practice modes. */
export class LocalPuzzleDriver {
  private state: PuzzleMatchState;
  private timer: number | null = null;
  private readonly options: LocalPuzzleDriverOptions;

  constructor(options: LocalPuzzleDriverOptions) {
    this.options = options;
    this.state = this.createState();
  }

  start(): void {
    if (this.timer !== null) return;
    this.options.onSnapshot(this.state);
    this.timer = window.setInterval(() => this.tick(), 1000 / PUZZLE_TICK_RATE);
  }

  stop(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
  }

  reset(): void {
    resetPuzzleMatchForDemo(this.state);
    this.options.onSnapshot(this.state);
  }

  snapshot(): PuzzleMatchState {
    return this.state;
  }

  destroy(): void {
    this.stop();
  }

  private createState(): PuzzleMatchState {
    const seed = this.options.seed ?? (this.options.mode === "duel" ? 431 : 887);
    const botDifficulty = this.options.botDifficulty ?? "medium";
    const baseSlots = this.options.mode === "duel" ? DUEL_SLOTS : TEAM_SLOTS;
    return createPuzzleMatchState({
      id: `local-puzzle-${this.options.mode}`,
      mode: this.options.mode,
      slots: baseSlots.map((slot) => ({
        ...slot,
        botDifficulty,
      })),
      seed
    });
  }

  private tick(): void {
    const inputs: Record<string, PuzzleInput> = {};
    for (const player of this.state.players) {
      if (player.kind === "human" && !this.options.automateHuman) {
        inputs[player.id] = this.options.readHumanInput();
      } else {
        inputs[player.id] = createPuzzleBotInput(
          this.state,
          player.id,
          this.options.botDifficulty ?? "medium",
        );
      }
    }
    if (Object.keys(inputs).length === 0) inputs.p1 = { ...PUZZLE_EMPTY_INPUT };
    const events = stepPuzzleMatch(this.state, inputs);
    if (events.length > 0) this.options.onEvents?.(events);
    this.options.onSnapshot(this.state);
  }
}

export const LOCAL_PUZZLE_DUEL_SLOTS: ReadonlyArray<Readonly<PuzzlePlayerSlot>> = DUEL_SLOTS;
export const LOCAL_PUZZLE_TEAM_SLOTS: ReadonlyArray<Readonly<PuzzlePlayerSlot>> = TEAM_SLOTS;
